import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Crown, Heart, Star, Brain, Compass, TreePine, 
  TrendingUp, Users, Calendar, Gift, Target
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = ({ user, onNavigateToGame, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [error, setError] = useState(null);
  const [sessionType, setSessionType] = useState('jwt'); // 'jwt' or 'google'
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [homepagePreference, setHomepagePreference] = useState('gameplay'); // 'gameplay' or 'projects'

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Load dashboard data with enhanced session support
  useEffect(() => {
    loadDashboard();
    detectSessionType();
  }, []);

  const detectSessionType = () => {
    const sessionToken = document.cookie.split(';').find(row => row.trim().startsWith('session_token='));
    const jwtToken = localStorage.getItem('planetary_fed_token');
    
    if (sessionToken) {
      setSessionType('google');
    } else if (jwtToken) {
      setSessionType('jwt');
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Enhanced authentication check
      let response;
      
      // First check for Google OAuth session
      const sessionCheck = await axios.get(`${API}/auth/session-check`);
      
      if (sessionCheck.data.authenticated) {
        // Use session-based auth
        response = await axios.get(`${API}/user/dashboard`);
      } else {
        // Fallback to JWT
        const token = localStorage.getItem('planetary_fed_token');
        if (!token) {
          throw new Error('No authentication found');
        }
        
        response = await axios.get(`${API}/user/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setDashboardData(response.data);
      setEditedProfile({
        avatar_name: user.avatar_name || '',
        real_name: user.real_name || '',
        bio: response.data.bio || ''
      });
      setHomepagePreference(response.data.homepage_preference || 'gameplay');
      
    } catch (error) {
      console.error('Dashboard load error:', error);
      setError(error.response?.data?.detail || 'Failed to load dashboard data');
      
      // If authentication fails, redirect to login
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (sessionType === 'google') {
        // Google OAuth logout
        await axios.post(`${API}/auth/google/logout`);
        // Clear cookie
        document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      } else {
        // JWT logout
        localStorage.removeItem('planetary_fed_token');
        localStorage.removeItem('planetary_fed_user');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      onLogout();
    }
  };

  const saveProfile = async () => {
    try {
      setLoading(true);
      
      let response;
      if (sessionType === 'google') {
        response = await axios.put(`${API}/user/profile`, editedProfile);
      } else {
        const token = localStorage.getItem('planetary_fed_token');
        response = await axios.put(`${API}/user/profile`, editedProfile, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setIsEditing(false);
      await loadDashboard(); // Reload to show updated data
      
    } catch (error) {
      console.error('Profile save error:', error);
      setError('Failed to save profile changes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{
             backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
             backgroundSize: 'cover',
             backgroundPosition: 'center center'
           }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center space-y-4">
          <div 
            className="w-16 h-16 mx-auto mb-4"
            style={{
              transform: `rotate(${spiralPhase}deg)`,
              filter: 'brightness(1.2) saturate(1.5)'
            }}
          >
            <GrantitudeSpiral size={64} />
          </div>
          <div 
            className="text-white text-xl font-medium"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 20px rgba(255, 255, 255, 0.8)'
            }}
          >
            Accessing consciousness streams...
          </div>
          <div 
            className="text-white/70 text-sm"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            {sessionType === 'google' ? 'Google Timeline Access' : 'Standard Timeline Access'}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{
             backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
             backgroundSize: 'cover',
             backgroundPosition: 'center center'
           }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center space-y-4 max-w-md mx-auto p-6">
          <div className="bg-red-900/40 backdrop-blur-xl border border-red-400/30 rounded-xl p-6">
            <h2 
              className="text-red-300 text-xl font-bold mb-4"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Timeline Access Disrupted
            </h2>
            <p 
              className="text-red-200 text-sm mb-4"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {error}
            </p>
            <div className="space-y-2">
              <Button 
                onClick={loadDashboard}
                className="w-full bg-red-600/80 hover:bg-red-600 text-white"
              >
                Retry Connection
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="w-full border-red-400/50 text-red-300 hover:bg-red-900/30"
              >
                Return to Timeline Portal
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/bqsdnut5_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2002_42_22%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral behind background */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(6) rotate(${spiralPhase * 0.5}deg)`,
            filter: 'blur(8px) saturate(1.8) brightness(1.4)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

      <div className="relative z-10 max-w-6xl mx-auto p-6">
        {/* Elegant Header */}
        <div className="text-center mb-12 pt-8">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <img 
              src={user.avatar_url}
              alt="Timeline avatar"
              className="w-20 h-20 rounded-full border-2 border-white/40 shadow-2xl"
            />
            <div>
              <h1 
                className="text-4xl font-bold text-white"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.4)'
                }}
              >
                {user.name}
              </h1>
              <p 
                className="text-purple-200 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Consciousness Level: {user.consciousness_level}
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          
          {/* Trust Resonance */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <Heart className="w-8 h-8 text-red-400" />
              <div>
                <h3 
                  className="text-white text-xl font-medium"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Trust Resonance
                </h3>
                <p className="text-white/70 text-sm">Community standing</p>
              </div>
            </div>
            
            <div className="text-center">
              <div 
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                {user.trust_score}
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-red-400 to-pink-400 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(100, (user.trust_score / 200) * 100)}%` }}
                ></div>
              </div>
              <p className="text-white/60 text-sm mt-2">
                {user.trust_score > 150 ? 'Retreat Eligible' : 'Building Trust'}
              </p>
            </div>
          </div>

          {/* Homepage Preference Settings */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <Target className="w-8 h-8 text-green-400" />
              <div>
                <h3 
                  className="text-white text-xl font-medium"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Homepage Preference
                </h3>
                <p className="text-white/70 text-sm">Default landing experience</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex space-x-3">
                <button
                  onClick={() => setHomepagePreference('gameplay')}
                  className={`flex-1 p-3 rounded-xl border transition-all ${
                    homepagePreference === 'gameplay'
                      ? 'bg-purple-500/30 border-purple-400 text-purple-200'
                      : 'bg-black/30 border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="text-center">
                    <Brain className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Gameplay</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setHomepagePreference('projects')}
                  className={`flex-1 p-3 rounded-xl border transition-all ${
                    homepagePreference === 'projects'
                      ? 'bg-cyan-500/30 border-cyan-400 text-cyan-200'
                      : 'bg-black/30 border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="text-center">
                    <Compass className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Projects</span>
                  </div>
                </button>
              </div>
              
              <Button
                onClick={async () => {
                  try {
                    await saveProfile();
                    alert(`Homepage preference updated to ${homepagePreference}!`);
                  } catch (error) {
                    setError('Failed to save preference');
                  }
                }}
                className="w-full bg-green-600/30 hover:bg-green-600/50 text-white border border-green-400/40"
                size="sm"
              >
                Save Preference
              </Button>
              
              <p className="text-white/60 text-xs text-center">
                Choose your default experience when opening the app
              </p>
            </div>
          </div>

          {/* Wisdom Accumulation */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <Crown className="w-8 h-8 text-yellow-400" />
              <div>
                <h3 
                  className="text-white text-xl font-medium"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Wisdom Points
                </h3>
                <p className="text-white/70 text-sm">Consciousness growth</p>
              </div>
            </div>
            
            <div className="text-center">
              <div 
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                {dashboardData?.metrics?.wisdom_accumulated || 0}
              </div>
              <p className="text-white/60 text-sm">Points accumulated</p>
            </div>
          </div>

          {/* Game Progress */}
          <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <Star className="w-8 h-8 text-cyan-400" />
              <div>
                <h3 
                  className="text-white text-xl font-medium"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Timeline Level
                </h3>
                <p className="text-white/70 text-sm">Game progression</p>
              </div>
            </div>
            
            <div className="text-center">
              <div 
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                {user.game_progress?.level || 1}
              </div>
              <p className="text-white/60 text-sm">of ∞ levels</p>
            </div>
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
          <div className="text-center space-y-6">
            <h2 
              className="text-3xl font-bold text-white"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 15px rgba(255, 255, 255, 0.3)'
              }}
            >
              Your Consciousness Journey
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={onNavigateToGame}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg transition-all duration-300"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Brain className="w-6 h-6 mr-3" />
                Continue the Game
              </Button>
              
              {user.trust_score > 150 && (
                <Button
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-8 py-4 text-lg transition-all duration-300"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Calendar className="w-6 h-6 mr-3" />
                  Retreat Access
                </Button>
              )}
              
              <Button
                onClick={onLogout}
                variant="outline"
                className="text-white border-white/40 hover:bg-white/10 px-8 py-4 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Return to Timeline
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;